const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

/*
 * Browser-only helpers. Call them from event handlers, never during render.
 * Every logo is self-contained SVG (colors, gradients, masks and filters live
 * inside the element), so a clone of the rendered node is a complete file.
 */
const serialize = (svg: SVGSVGElement, width: number, height: number): string => {
    const clone = svg.cloneNode(true) as SVGSVGElement

    clone.removeAttribute('class')
    clone.removeAttribute('style')
    clone.removeAttribute('aria-hidden')
    clone.removeAttribute('data-logo-export')
    clone.setAttribute('xmlns', SVG_NAMESPACE)
    clone.setAttribute('width', String(width))
    clone.setAttribute('height', String(height))

    return new XMLSerializer().serializeToString(clone)
}

const saveBlob = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = fileName
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    link.remove()

    window.setTimeout(() => URL.revokeObjectURL(url), 4000)
}

export const downloadSvg = (svg: SVGSVGElement, fileName: string): void => {
    const { width, height } = svg.viewBox.baseVal
    const markup = serialize(svg, width, height)

    saveBlob(new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }), `${fileName}.svg`)
}

export const downloadPng = (svg: SVGSVGElement, fileName: string, targetWidth: number): Promise<void> => {
    const { width, height } = svg.viewBox.baseVal
    const pixelWidth = Math.round(targetWidth)
    const pixelHeight = Math.round(targetWidth * (height / width))
    const markup = serialize(svg, pixelWidth, pixelHeight)

    return new Promise((resolve, reject) => {
        const image = new Image()

        image.onload = () => {
            const canvas = document.createElement('canvas')

            canvas.width = pixelWidth
            canvas.height = pixelHeight

            const context = canvas.getContext('2d')

            if (!context) {
                reject(new Error("Canvas is not available in this browser."))
                return
            }

            context.drawImage(image, 0, 0, pixelWidth, pixelHeight)
            canvas.toBlob(blob => {
                if (!blob) {
                    reject(new Error("The PNG could not be created."))
                    return
                }

                saveBlob(blob, `${fileName}.png`)
                resolve()
            }, 'image/png')
        }

        image.onerror = () => reject(new Error("The logo could not be rendered."))
        image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`
    })
}
